import type { Grain } from './boardTables';
import type { ValueFormat } from './cards';

/**
 * The workbook's "Metrics Filter" ("Select a metric"), as configuration.
 *
 * A metric names the one card column it sums. A tab lists the metrics its cards already return, so a
 * new measure from svc-reporting is one entry here, not new page code.
 */
export type BoardMetric = {
  label: string;
  format: ValueFormat;
  measure: string;
  /** Summable across rows and buckets. Distinct counts (orders) are not: an order can span categories. */
  additive: boolean;
};

export const METRICS = {
  net: { label: 'Net sales', format: 'currency', measure: 'net_sales_aud', additive: true },
  gross: { label: 'Gross sales', format: 'currency', measure: 'gross_sales_aud', additive: true },
  margin: { label: 'Gross margin (AR)', format: 'currency', measure: 'net_margin_aud', additive: true },
  refunds: { label: 'Refunds', format: 'currency', measure: 'refunds_aud', additive: true },
  orders: { label: 'Orders', format: 'count', measure: 'orders', additive: false },
  refundedOrders: { label: 'Orders refunded', format: 'count', measure: 'refunded_orders', additive: false },
} as const satisfies Record<string, BoardMetric>;

export type MetricId = keyof typeof METRICS;

type MetricOption = { metric: MetricId; cardId: string };
export type MetricTab = {
  /** The rows and time buckets of the tab's matrix. */
  dimension: { key: string; label: string };
  grain: Grain;
  /** What the tab showed before the selector, so the default view does not change. */
  default: MetricId;
  options: ReadonlyArray<MetricOption>;
};

const sums = (cardId: string): Array<MetricOption> =>
  (['net', 'gross', 'margin', 'refunds'] as const).map((metric) => ({ metric, cardId }));

/** Tabs that carry the workbook's Metrics Filter, with the card that answers each metric today. */
export const METRIC_TABS = {
  vertical: {
    dimension: { key: 'product_category', label: 'Product category' },
    grain: 'month',
    default: 'net',
    options: [
      ...sums('v_vertical'),
      { metric: 'orders', cardId: 'v_vertical_orders' },
      { metric: 'refundedOrders', cardId: 'v_vertical_orders' },
    ],
  },
  region: {
    dimension: { key: 'deal_region', label: 'Deal region' },
    grain: 'month',
    default: 'net',
    options: sums('r_region'),
  },
  supply: {
    dimension: { key: 'offer_country', label: 'Offer country' },
    grain: 'year',
    default: 'net',
    options: sums('y_supply'),
  },
  sale: {
    dimension: { key: 'customer_country', label: 'Customer country' },
    grain: 'year',
    default: 'net',
    options: sums('y_sale'),
  },
  refunds: {
    dimension: { key: 'deal_region', label: 'Deal region' },
    grain: 'quarter',
    default: 'refunds',
    options: [
      { metric: 'refunds', cardId: 'q_refunds' },
      { metric: 'refundedOrders', cardId: 'q_refund_orders' },
    ],
  },
} as const satisfies Record<string, MetricTab>;

export type MetricView = keyof typeof METRIC_TABS;
export const METRIC_VIEWS = Object.keys(METRIC_TABS) as Array<MetricView>;
export type BoardMetrics = Record<MetricView, MetricId>;

export const metricParam = (view: MetricView) => `${view}Metric`;

/** The metric a tab shows, falling back to its default for anything the tab cannot answer. */
export function metricOption(view: MetricView, metric: string | null | undefined): MetricOption {
  const tab: MetricTab = METRIC_TABS[view];
  return (
    tab.options.find((option) => option.metric === metric) ??
    tab.options.find((option) => option.metric === tab.default) ??
    tab.options[0]
  );
}

export function parseMetrics(search: URLSearchParams): BoardMetrics {
  return Object.fromEntries(
    METRIC_VIEWS.map((view) => [view, metricOption(view, search.get(metricParam(view))).metric]),
  ) as BoardMetrics;
}

/** Only a metric other than the default goes in the URL, so default links stay short. */
export function writeMetrics(metrics: BoardMetrics, search: URLSearchParams) {
  for (const view of METRIC_VIEWS) {
    const chosen = metricOption(view, metrics[view]).metric;
    if (chosen === METRIC_TABS[view].default) {
      search.delete(metricParam(view));
    } else {
      search.set(metricParam(view), chosen);
    }
  }
}
